import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { useMemo, useState, type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  useSitePhotos,
  useUploadPhoto,
  useWorkItemPrefill,
  workProgressApi,
  type WorkItemStatus,
  type WorkProgress,
} from '../../api/execution'
import { describeError } from '../../api/client'
import { ErrorBlock } from '../../components/Feedback'
import { TemplateBar } from '../../components/TemplateBar'
import { WorkStatusChip } from '../../components/ReportStatusChip'

// ------------------------------------------------------------- work tab

export function WorkTab({ reportId, editable }: { reportId: string; editable: boolean }) {
  const list = workProgressApi.useList(reportId)
  const create = workProgressApi.useCreate(reportId)
  const update = workProgressApi.useUpdate(reportId)
  const remove = workProgressApi.useDelete(reportId)
  const prefill = useWorkItemPrefill(reportId)
  const photos = useSitePhotos(reportId)
  const uploadPhoto = useUploadPhoto(reportId)
  const [open, setOpen] = useState(false)
  const [editingLine, setEditingLine] = useState<WorkProgress | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<WorkProgress | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)

  const available = useMemo(
    () => (prefill.data ?? []).filter((p) => !p.alreadyRecordedToday),
    [prefill.data],
  )
  // No BOQ is no longer a dead end - it only means every line has to be described.
  const boqEmpty = (prefill.data ?? []).length === 0

  // How many photographs back each recorded quantity.
  const photosByWorkLine = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of photos.data ?? []) {
      if (p.workProgressId) {
        map.set(p.workProgressId, (map.get(p.workProgressId) ?? 0) + 1)
      }
    }
    return map
  }, [photos.data])

  /**
   * Saves the quantity, then its photographs.
   *
   * <p>The work line is created first because each photo has to reference it. If an
   * upload then fails the quantity is still recorded and the engineer is told which
   * photographs did not make it, rather than losing the whole entry to a flaky site
   * connection. They are sent one at a time so a single large photo cannot push the
   * request past the server's limit and take the others down with it.</p>
   */
  async function saveWorkLine(body: Record<string, unknown>, photos: File[]) {
    setPhotoError(null)
    const created = await create.mutateAsync(body)
    const id = (created as { id: string }).id
    const failed: string[] = []

    for (const file of photos) {
      try {
        await uploadPhoto.mutateAsync({
          file,
          workProgressId: id,
          description: String(body.description ?? 'Work completed'),
        })
      } catch (err) {
        failed.push(`${file.name} (${describeError(err)})`)
      }
    }

    if (failed.length > 0) {
      setPhotoError(
        `The quantity was saved. ${failed.length} of ${photos.length} photograph` +
          `${photos.length === 1 ? '' : 's'} did not upload: ${failed.join('; ')}`,
      )
    }
  }

  return (
    <>
      <TemplateBar module="WORK" reportId={reportId} editable={editable} />
      <Toolbar
        title="Quantities completed today. Against a BOQ line they are measured - cumulative, balance and percentages are calculated. Other work is recorded as described."
        action={
          editable && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add work line
            </Button>
          )
        }
      />
      {/* Neither case blocks entry any more, so neither is an error. They are said
          because an engineer who expected a BOQ picker and found none should know why. */}
      {editable && !prefill.isLoading && available.length === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            boqEmpty ? (
              <Button size="small" component={RouterLink} to="/site-boq">
                Set up the BOQ
              </Button>
            ) : undefined
          }
        >
          {boqEmpty
            ? 'This site has no BOQ lines yet, so work can only be recorded by describing it. ' +
              'Add the bill under Execution → Site BOQ and the picker appears here immediately.'
            : 'Every BOQ line already has an entry today. Edit a line below to correct it, or ' +
              'add work that is not on the bill.'}
        </Alert>
      )}

      {list.error && <ErrorBlock error={list.error} />}
      {create.error && <ErrorBlock error={create.error} title="Could not save the work line" />}
      {update.error && <ErrorBlock error={update.error} title="Could not save the change" />}
      {remove.error && <ErrorBlock error={remove.error} title="Could not remove the work line" />}
      {photoError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPhotoError(null)}>
          {photoError}
        </Alert>
      )}
      <DataTable
        columns={['BOQ', 'Description', 'Unit', 'Planned today', 'Done today', 'Today %', 'Cumulative', 'Balance', 'Overall %', 'Status', 'Photos', '']}
        rows={(list.data ?? []).map((w: WorkProgress) => [
          w.workItemId ? (
            <span style={{ fontFamily: 'monospace' }}>{w.workItemCode}</span>
          ) : (
            <Chip label="Off BOQ" size="small" variant="outlined" />
          ),
          <span title={w.description}>{truncate(w.description, 44)}</span>,
          w.uomCode,
          num(w.todayPlannedQuantity),
          <Box>
            <strong>{num(w.todayCompletedQuantity)}</strong>
            {/* What was typed, when it said more than the number did. */}
            {w.remarks && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {truncate(w.remarks, 28)}
              </Typography>
            )}
          </Box>,
          w.todayProgressPct != null ? `${w.todayProgressPct}%` : '-',
          num(w.cumulativeCompletedQuantity),
          num(w.balanceQuantity),
          w.overallProgressPct != null ? `${w.overallProgressPct}%` : '-',
          <WorkStatusChip status={w.status} />,
          <PhotoCount count={photosByWorkLine.get(w.id) ?? 0} />,
          editable ? (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <IconButton size="small" onClick={() => setEditingLine(w)} title="Edit this quantity">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirmDelete(w)}
                title="Remove this work line"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null,
        ])}
        empty="No work recorded yet."
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <WorkForm
          available={available}
          boqEmpty={boqEmpty}
          pending={create.isPending || uploadPhoto.isPending}
          onCancel={() => setOpen(false)}
          onSave={async (body, photos) => {
            await saveWorkLine(body, photos)
            setOpen(false)
          }}
        />
      </Dialog>

      <Dialog open={Boolean(editingLine)} onClose={() => setEditingLine(null)} fullWidth maxWidth="sm">
        {editingLine && (
          <EditWorkForm
            line={editingLine}
            pending={update.isPending}
            onCancel={() => setEditingLine(null)}
            onSave={async (body) => {
              await update.mutateAsync({ id: editingLine.id, body })
              setEditingLine(null)
            }}
          />
        )}
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove this work line?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmDelete?.workItemCode ?? 'Off BOQ'} — {truncate(confirmDelete?.description, 80)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {confirmDelete?.workItemId
              ? "The quantity comes off today's total and off the cumulative figure for this BOQ line."
              : "The quantity comes off today's total. Nothing else is affected, since it was not booked against the bill."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={async () => {
              if (confirmDelete) await remove.mutateAsync(confirmDelete.id)
              setConfirmDelete(null)
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}


/**
 * Correcting a quantity already recorded today.
 *
 * <p>Which line the quantity belongs to is fixed: moving a quantity between BOQ lines is
 * a delete and a re-entry, not an edit, and treating it as one would silently shift
 * progress between two cumulative totals. An off-bill line has no BOQ line to move it
 * to, but its description is the only thing identifying it, so that stays editable.</p>
 *
 * <p>The planned quantity is not editable here. It comes from the template that opened
 * the day, and a plan that can be adjusted after the fact to match what happened is not
 * a plan.</p>
 */
/**
 * What somebody typed into the remarks box, split into the number and the words.
 *
 * <p>The column behind that box is numeric and four generated columns compute from it —
 * cumulative, balance, and both percentages — so a quantity has to come out of it. But
 * people measure in things the bill does not: two loads, half of block A, a few bundles.
 * Taking the leading number and keeping the sentence means neither the arithmetic nor
 * what they actually wrote is lost.</p>
 *
 * <p>"8 loads" gives 8 and a note. "12.5" gives 12.5 and no note, because repeating the
 * number as a note would only be noise in the table.</p>
 */
function readQuantity(typed: string): { quantity: number; note: string | null } {
  const text = typed.trim()
  const match = text.match(/-?\d+(?:\.\d+)?/)
  const quantity = match ? Number(match[0]) : 0
  // A note only when the words say something the number does not.
  const note = match && match[0] === text ? null : text || null
  return { quantity: Number.isFinite(quantity) ? quantity : 0, note }
}

function EditWorkForm({
  line,
  pending,
  onCancel,
  onSave,
}: {
  line: WorkProgress
  pending: boolean
  onCancel: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}) {
  const offBill = !line.workItemId
  const [description, setDescription] = useState(line.description ?? '')
  // Seeded from the note when there was one, so the words come back for editing.
  const [todayDone, setTodayDone] = useState(
    line.remarks?.trim() || String(line.todayCompletedQuantity ?? ''),
  )
  const [status, setStatus] = useState<WorkItemStatus>(line.status ?? 'PROCESSING')

  const { quantity: done, note } = readQuantity(todayDone)
  const cumulative = Number(line.previousCompletedQuantity) + done
  // Nothing to exceed when there is no plan.
  const over = line.totalPlannedQuantity != null && cumulative > Number(line.totalPlannedQuantity)

  return (
    <>
      <DialogTitle>
        {offBill ? 'Edit work off the BOQ' : `Edit ${line.workItemCode}`}
        <Typography variant="body2" color="text.secondary">
          {truncate(line.description, 80)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {offBill ? (
          <TextField
            label="What was done"
            required
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={description.trim() === ''}
            helperText={
              description.trim() === ''
                ? 'This line is not on the BOQ, so its description is all that identifies it'
                : ' '
            }
            sx={{ mb: 2 }}
          />
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Planned total <strong>{num(line.totalPlannedQuantity)} {line.uomCode}</strong> · done
            before today <strong>{num(line.previousCompletedQuantity)}</strong>
          </Alert>
        )}

        <TextField
          label="Remarks"
          required
          fullWidth
          multiline
          minRows={3}
          value={todayDone}
          onChange={(e) => setTodayDone(e.target.value)}
          error={over}
          helperText={
            over
              ? 'Exceeds the planned quantity'
              : `A number, or words like "8 loads"${line.uomCode ? ` — measured in ${line.uomCode}` : ''}`
          }
        />

        <TextField
          select
          label="Status"
          fullWidth
          value={status}
          onChange={(e) => setStatus(e.target.value as WorkItemStatus)}
          sx={{ mt: 2 }}
        >
          {WORK_STATUSES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>

        {!offBill && todayDone !== '' && (
          <Alert severity={over ? 'error' : 'success'} sx={{ mt: 1 }}>
            Cumulative becomes <strong>{num(cumulative)}</strong> of{' '}
            {num(line.totalPlannedQuantity)}
            {over && ' — a manager must authorise the overrun before this can be saved.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            pending || todayDone.trim() === '' || (offBill && description.trim() === '')
          }
          onClick={() =>
            onSave({
              workItemId: line.workItemId ?? null,
              uomId: line.uomId,
              todayCompletedQuantity: done,
              remarks: note,
              status,
              description: offBill ? description.trim() : line.description,
            })
          }
        >
          {pending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </>
  )
}

function PhotoCount({ count }: { count: number }) {
  if (count === 0) {
    return <span style={{ color: '#9aa5ae' }}>—</span>
  }
  return (
    <Chip
      icon={<PhotoCameraIcon />}
      label={count}
      size="small"
      sx={{ fontWeight: 700, bgcolor: '#dcf2e6', color: '#0b5d3b' }}
    />
  )
}

/**
 * Recording a quantity, either against a BOQ line or not.
 *
 * <p>Most work is on the bill and measured against it. The rest of it — clearing a slip,
 * shifting scaffolding, a variation nobody has priced yet — still happened, and a form
 * that cannot hold it is one where people book it against the nearest BOQ line and
 * quietly corrupt that line's cumulative total. So it gets its own mode, where the
 * description and unit do the work the BOQ line would otherwise have done.</p>
 */
/**
 * Recording a quantity: what the work was, and how much of it got done.
 *
 * <p>Nothing else is asked for here. The planned quantity comes from the template, so
 * that the plan is set once for the day rather than retyped per line; status, delay
 * reason and remarks were fields most people skipped and the rest filled inconsistently,
 * which is worse than not collecting them. What is left is the two things an engineer
 * standing on site actually knows: which work, and how much.</p>
 *
 * <p>Most work is on the bill and measured against it. The rest of it — clearing a slip,
 * shifting scaffolding, a variation nobody has priced yet — still happened, and a form
 * that cannot hold it is one where people book it against the nearest BOQ line and
 * quietly corrupt that line's cumulative total. So it gets its own mode, where the
 * description and unit do the work the BOQ line would otherwise have done.</p>
 */

/**
 * How this piece of work stands, in the four words a site actually uses.
 *
 * <p>Planned and Processing and Completed are self-evident; Pending is the one that
 * earns its place — it means stalled, waiting on a drawing or a material or a decision,
 * and it replaced a Delayed/On-hold pair that drew a distinction nobody made.</p>
 */
const WORK_STATUSES: { value: WorkItemStatus; label: string }[] = [
  { value: 'PLANNED', label: 'Planned' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PENDING', label: 'Pending' },
]
function WorkForm({
  available,
  boqEmpty,
  pending,
  onCancel,
  onSave,
}: {
  available: ReturnType<typeof useWorkItemPrefill>['data']
  boqEmpty: boolean
  pending: boolean
  onCancel: () => void
  onSave: (body: Record<string, unknown>, photos: File[]) => Promise<void>
}) {
  // Other work is the default: most of what gets typed here is not a bill line, and
  // boqEmpty only ever forced this on anyway.
  const [offBill, setOffBill] = useState(true)
  const [photos, setPhotos] = useState<File[]>([])
  const [workItemId, setWorkItemId] = useState('')
  const [description, setDescription] = useState('')
  const [todayDone, setTodayDone] = useState('')
  const [status, setStatus] = useState<WorkItemStatus>('PROCESSING')

  const item = (available ?? []).find((a) => a.workItemId === workItemId)
  const { quantity: done, note } = readQuantity(todayDone)
  const cumulative = item ? Number(item.previousCompletedQuantity) + done : 0
  const over = item ? cumulative > Number(item.totalPlannedQuantity) : false
  // Off-BOQ work is recorded as a lump sum; the server supplies that unit, so the
  // quantity box carries no unit of its own.
  const unit = offBill ? undefined : item?.uomCode

  const ready = offBill
    ? description.trim() !== '' && todayDone.trim() !== ''
    : workItemId !== '' && todayDone.trim() !== ''

  return (
    <>
      <DialogTitle>Record work completed</DialogTitle>
      <DialogContent dividers>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={offBill ? 'off' : 'boq'}
          onChange={(_, v) => {
            if (v) setOffBill(v === 'off')
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="boq" disabled={boqEmpty}>
            Against a BOQ line
          </ToggleButton>
          <ToggleButton value="off">Other work</ToggleButton>
        </ToggleButtonGroup>

        {offBill ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This is recorded, not measured: there is no BOQ quantity to compare it with, so
              no balance or percentage is produced for it.
            </Alert>
            <TextField
              label="What was done"
              required
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g. Cleared slip at the gate approach"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
          </>
        ) : (
          <>
            <TextField
              select
              label="BOQ line"
              required
              fullWidth
              value={workItemId}
              onChange={(e) => setWorkItemId(e.target.value)}
              sx={{ mb: 2 }}
            >
              {(available ?? []).map((a) => (
                <MenuItem key={a.workItemId} value={a.workItemId}>
                  {a.code} — {truncate(a.description, 60)}
                </MenuItem>
              ))}
            </TextField>

            {item && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Planned total <strong>{num(item.totalPlannedQuantity)} {item.uomCode}</strong> ·
                already done <strong>{num(item.previousCompletedQuantity)}</strong> ·
                balance <strong>{num(item.balanceQuantity)}</strong>
                {item.overallProgressPct != null && <> · at <strong>{item.overallProgressPct}%</strong></>}
              </Alert>
            )}
          </>
        )}

        <TextField
          label="Remarks"
          required
          fullWidth
          multiline
          minRows={3}
          value={todayDone}
          onChange={(e) => setTodayDone(e.target.value)}
          error={over}
          helperText={
            over
              ? 'Exceeds the planned quantity'
              : `A number, or words like "8 loads"${unit ? ` — measured in ${unit}` : ''}`
          }
        />

        <TextField
          select
          label="Status"
          fullWidth
          value={status}
          onChange={(e) => setStatus(e.target.value as WorkItemStatus)}
          sx={{ mt: 2 }}
        >
          {WORK_STATUSES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>

        {!offBill && item && todayDone !== '' && (
          <Alert severity={over ? 'error' : 'success'} sx={{ mt: 1 }}>
            Cumulative becomes <strong>{num(cumulative)}</strong> of {num(item.totalPlannedQuantity)},
            leaving <strong>{num(Number(item.totalPlannedQuantity) - cumulative)}</strong>
            {over && ' — a manager must authorise the overrun before this can be saved.'}
          </Alert>
        )}

        <PhotoPicker
          photos={photos}
          onChange={setPhotos}
          hint={
            offBill
              ? 'Worth attaching — nothing on the bill vouches for this work'
              : 'Optional — proves the quantity you are recording'
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending || !ready}
          onClick={() =>
            onSave(
              {
                workItemId: offBill ? null : workItemId,
                todayCompletedQuantity: done,
                remarks: note,
                status,
                description: offBill ? description.trim() : item?.description,
              },
              photos,
            )
          }
        >
          {pending
            ? 'Saving...'
            : photos.length > 0
              ? `Save work line and ${photos.length} photo${photos.length === 1 ? '' : 's'}`
              : 'Save work line'}
        </Button>
      </DialogActions>
    </>
  )
}

/**
 * Attaching photographs to a work line.
 *
 * <p>Several, not one: a pour, a level and a finished face are three different pieces of
 * evidence for the same quantity, and making someone save the line three times to attach
 * them is how they end up attaching none. Each is uploaded as its own request, which also
 * keeps every request inside the server's upload limit however many are picked.</p>
 *
 * <p>The camera is offered separately from the gallery because on a phone those are
 * genuinely different acts: one is taken now at the face of the work, the other is chosen
 * from what was taken earlier.</p>
 */
function PhotoPicker({
  photos,
  onChange,
  hint,
}: {
  photos: File[]
  onChange: (next: File[]) => void
  hint: string
}) {
  function add(list: FileList | null) {
    if (!list || list.length === 0) return
    onChange([...photos, ...Array.from(list)])
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />}>
          Take photo
          <input
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              add(e.target.files)
              e.target.value = ''
            }}
          />
        </Button>
        <Button component="label" variant="outlined" startIcon={<AddPhotoAlternateIcon />}>
          Add from gallery
          <input
            hidden
            multiple
            type="file"
            accept="image/*"
            onChange={(e) => {
              add(e.target.files)
              // Cleared so picking the same file twice in a row still fires onChange.
              e.target.value = ''
            }}
          />
        </Button>
        <Typography variant="body2" color="text.secondary">
          {photos.length === 0
            ? hint
            : `${photos.length} attached${photos.length > 1 ? ' — uploaded one by one' : ''}`}
        </Typography>
      </Box>

      {photos.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {photos.map((p, i) => (
            <Chip
              key={`${p.name}-${i}`}
              label={`${truncate(p.name, 22)} (${Math.round(p.size / 1024)} KB)`}
              onDelete={() => onChange(photos.filter((_, j) => j !== i))}
              size="small"
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

// --------------------------------------------------------------- helpers

function Toolbar({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {title}
      </Typography>
      {action}
    </Box>
  )
}

function DataTable({
  columns,
  rows,
  empty,
}: {
  columns: string[]
  rows: ReactNode[][]
  empty: string
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell key={i}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, r) => (
            <TableRow key={r} hover>
              {cells.map((cell, c) => (
                <TableCell key={c}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function num(value?: number | null): string {
  if (value == null) return '-'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
}

function truncate(text: string | undefined, max: number): string {
  if (!text) return '-'
  return text.length > max ? `${text.slice(0, max)}...` : text
}
